import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck, ShieldAlert, KeyRound, Search, ArrowRight, Loader2,
  CheckCircle2, XCircle, AlertTriangle, ArrowLeft, Hash,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "../api/client";

const verdictMap = {
  safe:        { label: "SEGURO",     color: "#8BF56C", Icon: CheckCircle2 },
  suspicious:  { label: "SOSPECHOSO", color: "#F4D03F", Icon: AlertTriangle },
  manipulated: { label: "MANIPULADO", color: "#FF5A6E", Icon: XCircle },
};

const SHA_RE = /^[0-9a-fA-F]{64}$/;

export default function Verify() {
  const { hash: paramHash } = useParams();
  const navigate = useNavigate();
  const [hash, setHash] = useState(paramHash || "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runVerify = async (h) => {
    const clean = (h || "").trim().toLowerCase();
    if (!SHA_RE.test(clean)) {
      setError("Introduce un SHA-256 válido (64 caracteres hexadecimales).");
      setResult(null);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/verify/${clean}`);
      setResult(data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Error al consultar el verificador";
      setError(msg);
      setResult(null);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // Auto-run when hash provided in URL
  useEffect(() => {
    if (paramHash && SHA_RE.test(paramHash)) {
      setHash(paramHash);
      runVerify(paramHash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramHash]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (hash !== paramHash) {
      navigate(`/verify/${hash.trim().toLowerCase()}`);
    } else {
      runVerify(hash);
    }
  };

  const v = result?.exists ? (verdictMap[result.verdict] || verdictMap.suspicious) : null;
  const VerdictIcon = v?.Icon;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="verify-page">
      {/* Header */}
      <header className="border-b border-[#1F2428] bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
          <button
            data-testid="verify-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-[#8A8F98] hover:text-[#DEFF9A] transition-colors text-sm"
          >
            <ArrowLeft size={16} /> ScanEye
          </button>
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#5A5F68]">
            Verificador público · v3.3
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        {/* Hero */}
        <div className="mb-8 text-center sm:text-left">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-[#DEFF9A]/10 border border-[#DEFF9A]/30">
            <ShieldCheck size={14} className="text-[#DEFF9A]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#DEFF9A]">
              Verificación pública del dictamen
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tighter mb-2">
            ¿Existe un <span className="text-[#DEFF9A]">dictamen ScanEye</span> para este archivo?
          </h1>
          <p className="text-[#8A8F98] leading-relaxed text-sm sm:text-base">
            Introduce el <b>SHA-256</b> que aparece al inicio del informe pericial y comprueba
            su existencia, fecha y veredicto. Útil para validar la autenticidad de un dictamen
            recibido de la parte contraria sin necesidad de acceso a la cuenta original.
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmit}
          data-testid="verify-form"
          className="bg-[#0A0A0A] border border-[#1F2428] rounded-2xl p-5 sm:p-6 mb-6"
        >
          <label className="flex items-center gap-1.5 mb-2">
            <KeyRound size={12} className="text-[#DEFF9A]" />
            <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#DEFF9A]">
              SHA-256 del archivo (64 hex)
            </span>
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Hash size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5A5F68]" />
              <input
                data-testid="verify-hash-input"
                type="text"
                value={hash}
                onChange={(e) => setHash(e.target.value)}
                placeholder="ej.: 9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08"
                className="w-full bg-[#141414] border border-[#1F2428] focus:border-[#DEFF9A] rounded-lg pl-9 pr-3 py-3 text-sm font-mono text-white placeholder:text-[#5A5F68] outline-none transition-all"
              />
            </div>
            <button
              data-testid="verify-submit-btn"
              type="submit"
              disabled={loading}
              className="bg-[#DEFF9A] hover:bg-[#C8FF6E] text-black font-bold rounded-lg px-5 py-3 transition-colors flex items-center justify-center gap-2 text-sm disabled:opacity-50 sm:min-w-[160px]"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {loading ? "Verificando..." : "Verificar dictamen"}
            </button>
          </div>
          {error && (
            <p data-testid="verify-error" className="mt-3 text-[11px] font-mono uppercase tracking-[0.18em] text-[#FF5A6E]">
              ⚠ {error}
            </p>
          )}
        </form>

        {/* Result */}
        {result && result.exists && (
          <div
            data-testid="verify-result-found"
            className="rounded-2xl p-5 sm:p-7 border-2 mb-6"
            style={{ borderColor: v.color, background: `${v.color}11` }}
          >
            <div className="flex items-center gap-3 mb-4">
              <VerdictIcon size={28} style={{ color: v.color }} />
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] mb-0.5" style={{ color: v.color }}>
                  Dictamen verificado
                </div>
                <div className="font-display text-2xl tracking-tighter leading-none" style={{ color: v.color }}>
                  {v.label}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#8A8F98]">Veracidad</div>
                <div className="font-mono text-3xl font-bold" style={{ color: v.color }}>
                  {result.truthfulness_score}%
                </div>
              </div>
            </div>

            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-xs mt-4 pt-4 border-t border-white/5">
              <dt className="font-mono uppercase tracking-[0.15em] text-[#8A8F98]">ID informe</dt>
              <dd className="font-mono text-white">{result.report_id_short}</dd>
              <dt className="font-mono uppercase tracking-[0.15em] text-[#8A8F98]">Tipo de medio</dt>
              <dd className="font-mono text-white">{result.media_type}</dd>
              <dt className="font-mono uppercase tracking-[0.15em] text-[#8A8F98]">Fecha de análisis</dt>
              <dd className="font-mono text-white">{result.analyzed_at && new Date(result.analyzed_at).toLocaleString("es-ES")}</dd>
              <dt className="font-mono uppercase tracking-[0.15em] text-[#8A8F98]">Emisor</dt>
              <dd className="font-mono text-white">{result.issuer}</dd>
            </dl>

            <p className="mt-5 text-xs text-[#8A8F98] leading-relaxed">
              {result.message}
            </p>
          </div>
        )}

        {result && !result.exists && (
          <div
            data-testid="verify-result-missing"
            className="rounded-2xl p-5 sm:p-7 border-2 mb-6"
            style={{ borderColor: "#FF5A6E", background: "rgba(255,90,110,0.06)" }}
          >
            <div className="flex items-start gap-3">
              <ShieldAlert size={26} className="text-[#FF5A6E] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] mb-1 text-[#FF5A6E]">
                  Sin dictamen asociado
                </div>
                <h2 className="font-display text-xl tracking-tight mb-2 text-[#FF5A6E]">
                  No existe ningún dictamen ScanEye para este SHA-256
                </h2>
                <p className="text-sm text-[#8A8F98] leading-relaxed">
                  El hash introducido no corresponde a ningún informe pericial registrado.
                  Verifica que has copiado el SHA-256 completo (64 caracteres) y que procede
                  de un dictamen emitido por el motor pericial de ScanEye.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="bg-gradient-to-br from-[#DEFF9A]/10 to-transparent border border-[#DEFF9A]/30 rounded-xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#DEFF9A] mb-1">
              ¿Tu propio archivo?
            </div>
            <p className="text-sm text-[#8A8F98] leading-relaxed">
              Sube tu imagen, vídeo o PDF y obtén tu propio dictamen pericial en menos de un minuto.
            </p>
          </div>
          <button
            data-testid="verify-cta-scan"
            onClick={() => navigate("/")}
            className="bg-[#DEFF9A] hover:bg-[#C8FF6E] text-black font-bold rounded-lg px-5 py-2.5 text-sm transition-colors flex items-center gap-2"
          >
            Analizar mi archivo <ArrowRight size={14} />
          </button>
        </div>

        <p className="mt-8 text-center text-[10px] font-mono uppercase tracking-[0.22em] text-[#5A5F68]">
          ScanEye Labs · Verificación forense · info@scaneye.es
        </p>
      </main>
    </div>
  );
}
